// ----------------------------------------------------------------------
// <copyright file="Izenda.LazyLoadingUtils.js" company="Izenda">
//  Copyright (c) 2015 Izenda, Inc.
//  ALL RIGHTS RESERVED
//
//  The entire contents of this file is protected by U.S. and
//  International Copyright Laws. Unauthorized reproduction,
//  reverse-engineering, and distribution of all or any portion of
//  the code contained in this file is strictly prohibited and may
//  result in severe civil and criminal penalties and will be
//  prosecuted to the maximum extent possible under the law.
//
//  RESTRICTIONS
//
//  THIS SOURCE CODE AND ALL RESULTING INTERMEDIATE FILES
//  ARE CONFIDENTIAL AND PROPRIETARY TRADE
//  SECRETS OF IZENDA INC. THE REGISTERED DEVELOPER IS
//  LICENSED TO DISTRIBUTE THE PRODUCT AND ALL ACCOMPANYING .NET
//  CONTROLS AS PART OF AN EXECUTABLE PROGRAM ONLY.
//
//  THE SOURCE CODE CONTAINED WITHIN THIS FILE AND ALL RELATED
//  FILES OR ANY PORTION OF ITS CONTENTS SHALL AT NO TIME BE
//  COPIED, TRANSFERRED, SOLD, DISTRIBUTED, OR OTHERWISE MADE
//  AVAILABLE TO OTHER INDIVIDUALS WITHOUT EXPRESS WRITTEN CONSENT
//  AND PERMISSION FROM IZENDA INC.
//
//  CONSULT THE END USER LICENSE AGREEMENT(EULA FOR INFORMATION ON
//  ADDITIONAL RESTRICTIONS.
//  </copyright>
// ----------------------------------------------------------------------

import { observable, action } from 'mobx';
import {
  isEmpty,
  isNil,
  includes,
  find,
  forEach,
  some,
  clone,
  filter,
  flow,
  uniqueId,
  map,
  pick,
  every,
  reduce
} from 'lodash/fp';

export class TreeNode {
  @observable nodeType: string = '';
  @observable source: any = null;
  @observable isLeafNode: boolean = false;
  @observable showTotal: boolean = false;
  @observable expand: boolean = false;
  @observable disabled: boolean = false;

  constructor(data: any) {
    this.map = {};
    this.childNodes = [];
    this.populateData(data);
  }

  /**
   * Populate data
   * @param  {Object} data
   */
  @action
  populateData(data: any) {
    this.nodeType = data.mapItem.nodeType;
    this.source = data.child;
    this.expand = isNil(data.child.expand) ? data.mapItem.expand : data.child.expand;
    this.isLeafNode = isNil(data.mapItem.children);
    this.showTotal = data.mapItem.showTotal;
    this.disabled = data.mapItem.disabledIfNoChild && isEmpty(data.childNodes);
    this.childNodes = data.childNodes;
    this.map = data.mapItem;
  }

  /**
   * Change state tree node
   */
  onChange(source: any, parent: any, event: any): any {
    return onChangeNode(this.map.children, source, parent, event);
  }

  /**
   * Get Child Nodes
   * @return {Array}
   */
  getChildNodes(): Array<any> {
    return this.isLeafNode ? [] : this.childNodes;
  }
}

/**
 * Concat tree
 * @param {Array} tree
 * @param {Array} newData
 * @param {Object} treeMap
 * @param {Number} numOfChilds
 * @param {Array} parentIds
 */
export function concatTree(
  tree: Array<any>,
  newData: Array<any>,
  treeMap: Array<any>,
  numOfChilds: number,
  parentIds: Array<string>
) {
  // Append new data when original tree is empty
  if (isEmpty(tree)) {
    newData.forEach(function(data, index) {
      tree.push(data);
      if (index === newData.length - 1) {
        createVirtualNode(tree, data, treeMap, numOfChilds, parentIds);
      }
    });

    return;
  }

  // recursive to concat data to original tree base on tree map
  recursiveConcat(tree, newData, treeMap, numOfChilds, parentIds, null, false);

  function recursiveConcat(recTree, recData, recMap, numOfChilds, parentIds, parentStatus, isLastPage) {
    recData.forEach(function(data, index) {
      const node = find(['id', data.id], recTree);

      // check node existing in original tree
      if (node) {
        forEach(function(map) {
          // Create next parent id array
          const nextParentIds = clone(parentIds);
          nextParentIds.push(data.id);
          const status = {
            checked: node.checked,
            indeterminate: node.indeterminate
          };

          // continue to recursive concat for while this node doesn't existing in original tree
          recursiveConcat(
            node[map.prop],
            data[map.prop],
            map.children,
            data.numOfChilds,
            nextParentIds,
            status,
            data.isLastPage
          );
        }, recMap);
      } else {
        // if original tree branch has virtual node, it always remove virtual node first
        some('isVirtualNode', recTree) && recTree.splice(recTree.length - 1, 1);

        // Update child checked if parent status with indeterminate flase or checked is true
        if (parentStatus && !isNil(parentStatus.checked) && !(!parentStatus.checked && parentStatus.indeterminate)) {
          updateChildChecked(data, parentStatus.checked);
        }
        // append data to original tree branch
        recTree.push(data);

        // append a virtual node at last position
        if (!isLastPage && index === recData.length - 1) {
          createVirtualNode(recTree, data, recMap, numOfChilds, parentIds);
        }
      }
    });

    // If data for a virtual node is empty, we also remove this virtual node.
    isEmpty(recData) && some('isVirtualNode', recTree) && recTree.splice(recTree.length - 1, 1);
  }
}

/**
 * Update child checked
 * @param  {Object} data
 * @param  {boolean} checked
 */
function updateChildChecked(data, checked) {
  data.checked = checked;
  data.indeterminate = false;
  data.numOfCheckedChilds = checked ? data.numOfChilds : 0;
  data.childNodes && data.childNodes.forEach(child => updateChildChecked(child, checked));
}

/**
 * Update Tree Node
 * @param  {Array} tree
 * @param  {Array} newTree
 * @param  {Array} treeMap
 * @param  {Array} props
 */
export function updateTree(tree: Array<any>, newTree: Array<any>, treeMap: Array<any>, props: any) {
  if (isEmpty(newTree)) {
    return;
  }

  // recursive to concat data to original tree base on tree map
  recursiveUpdate(tree, newTree, treeMap);

  function recursiveUpdate(recTree, recData, recMap) {
    forEach(data => {
      var node = find(['id', data.id], recTree);

      // check node existing in original tree
      if (node) {
        const numOfCheckedChilds = reduce((total, item) => (item.checked ? total + 1 : total), 0, data.childNodes);
        if (
          data.checked ||
          !(data.checked || data.indeterminate) ||
          (data.numOfCheckedChilds === numOfCheckedChilds && data.interacted)
        ) {
          node.childNodes && node.childNodes.forEach(child => {
            child.checked = data.checked;
            child.indeterminate = false;
          });
        }
        // Update value
        const keys = Object.keys(node);
        for (let i = 0; i < keys.length; i++) {
          const key = keys[i];
          if (needUpdate(key) && typeof node[key] !== 'object' && node[key] !== data[key]) {
            node[key] = data[key];
          }
        }

        forEach(function(map) {
          // continue to recursive concat for while this node doesn't existing in original tree
          recursiveUpdate(node[map.prop], data[map.prop], map.children);
        }, recMap);
      }
    }, recData);
  }

  function needUpdate(key) {
    return isNil(props) ? true : includes(key, props);
  }
}

/**
 * Concat array
 * @param  {Array} tree
 * @param  {Array} data
 * @param  {String} key
 * @param  {Boolean} needVirtualNode
 * @param  {Number} virtualNodeIgnoredItemsCount
 * @return {Array} tree modified
 */
export function concatArray(tree: Array<any>, data: Array<any>, key: string, needVirtualNode: boolean,
                            virtualNodeIgnoredItemsCount: number) {
  if (isEmpty(tree)) {
    data.forEach((d, index) => {
      tree.push(d);
      if (needVirtualNode && index === data.length - 1) {
        // Calculate tree length to have correct items count
        let treeLen = tree.length;
        if (!isNil(virtualNodeIgnoredItemsCount) && virtualNodeIgnoredItemsCount > 0) {
          treeLen -= virtualNodeIgnoredItemsCount;
        }
        tree.push(virtualNode([], treeLen));
      }
    });
    return tree;
  }

  // if original tree branch has virtual node, it always remove virtual node first
  some('isVirtualNode', tree) && tree.splice(tree.length - 1, 1);

  // append data to original tree branch
  data.forEach(i => !some({ [key]: i[key] }, tree) && tree.push(i));

  // Calculate tree length to have correct items count
  let treeLen = tree.length;
  if (!isNil(virtualNodeIgnoredItemsCount) && virtualNodeIgnoredItemsCount > 0) {
    treeLen -= virtualNodeIgnoredItemsCount;
  }

  // append a virtual node at last position
  needVirtualNode && tree.push(virtualNode([], treeLen));
  return tree;
}

/**
 * Create virtual node
 * @param {any} recTree
 * @param {any} data
 * @param {any} recMap
 * @param {any} numOfChilds
 * @param {any} parentIds
 */
function createVirtualNode(
  recTree: Array<any>,
  data: Array<any>,
  recMap: Array<any>,
  numOfChilds: number,
  parentIds: Array<string>
) {
  // Create virtual node for itself
  if (numOfChilds !== recTree.length) {
    recTree.push(virtualNode(parentIds, recTree.length));
  }

  parentIds.push(data.id);
  recursiveCreate(data, recMap, parentIds);

  function recursiveCreate(data, recMap, parentIds) {
    forEach(function(map) {
      var nextNodeList = data[map.prop];
      if (!isEmpty(nextNodeList)) {
        var latestNode = nextNodeList[nextNodeList.length - 1];
        var nextParentIds = clone(parentIds);
        nextParentIds.push(latestNode.id);
        recursiveCreate(latestNode, map.children, nextParentIds);
        if (!data.isLastPage && data.numOfChilds !== nextNodeList.length) {
          nextNodeList.push(virtualNode(parentIds, nextNodeList.length));
        }
      }
    }, recMap);
  }
}

function virtualNode(parentIds: Array<string>, fromIndex: number) {
  return {
    id: uniqueId('VirtualNode_'),
    name: 'Virtual Node',
    isVirtualNode: true,
    isRendering: false,
    parentIds: parentIds,
    fromIndex: fromIndex
  };
}

/**
 * Build report tree
 * @param {Object} props
 * @param {Object} data
 * @returns {Object} tree
 */
export function parseToTree(data: Array<any>, treeMap: Array<any>) {
  var getChildren = function(mapItem, firstNode, parentId, child) {
    var childNodes = [],
      parentStatus = this;

    if (parentStatus && !parentStatus.indeterminate) {
      child.checked = parentStatus.checked;
      child.indeterminate = false;
    }
    if (!child.indeterminate) {
      child.numOfCheckedChilds = child.checked ? child.numOfChilds : 0;
    }

    isNil(child.name) && (child.name = mapItem.defaultName);
    var status = {
      checked: child.checked,
      indeterminate: child.indeterminate
    };

    var isLeafNode = isNil(mapItem.children);
    !isLeafNode &&
      forEach(function(m) {
        var nodes = flow(
          map(getChildren.bind(status, m, false, child.id)),
          filter(function(c) {
            return !isNil(c);
          })
        )(child[m.prop]);

        childNodes = childNodes.concat(nodes);
      }, mapItem.children);

    return new TreeNode({
      mapItem: mapItem,
      child: child,
      firstNode: firstNode,
      childNodes: childNodes
    });
  };

  var childNodes = [];
  forEach(function(mapItem) {
    var nodes = flow(
      map(getChildren.bind(null, mapItem, true, null)),
      filter(function(c) {
        return !isNil(c);
      })
    )(data);
    childNodes = childNodes.concat(nodes);
  }, treeMap);

  return childNodes;
}

/**
 * onchange node
 * @param {Array} treeMap
 * @param {Object} source
 * @param {Object} parent
 * @param {Object} input
 */
function onChangeNode(childrenMap: Array<any>, source: any, parent: any, e: any) {
  const check = e.target.checked;
  const previousStatus = getPreviousStatus(source);

  updateNodeInfo(source, check);
  updateChildNodeInfo(source, check, childrenMap);
  updateParentNode(parent, check, previousStatus, source.indeterminate);
}

/**
 * Get previous status
 * @param  {Object} source
 * @return {Boolean}
 */
function getPreviousStatus(source: any): boolean {
  return source.indeterminate ? false : source.checked;
}

/**
 * Update node information
 * @param {Object} source
 * @param {Boolean} check
 */
function updateNodeInfo(source: any, check: boolean) {
  source.checked = check;
  !isNil(source.indeterminate) && (source.indeterminate = false);
  !isNil(source.numOfCheckedChilds) &&
    !isNil(source.numOfChilds) &&
    (check ? (source.numOfCheckedChilds = source.numOfChilds) : (source.numOfCheckedChilds = 0));
}

/**
 * Update child node information
 * @param {Object} source
 * @param {Boolean} check
 * @param {Array} treeMap
 * @param {Number} level
 */
function updateChildNodeInfo(source: any, check: boolean, childrenMap: Array<any>) {
  forEach(function(map) {
    forEach(function(child) {
      updateNodeInfo(child, check);
      updateChildNodeInfo(child, check, map.children);
    }, source[map.prop]);
  }, childrenMap);
}

/**
 * update parent node
 * @param {Object} parent
 * @param {Object} check
 */
function updateParentNode(parent: any, check: boolean, previousStatus: boolean, parentIndeterminate: boolean) {
  if (!isNil(parent)) {
    const parentSource = parent.data.source;
    if (parentIndeterminate) {
      previousStatus && parentSource.numOfCheckedChilds--;
      previousStatus = getPreviousStatus(parentSource);
      parentSource.indeterminate = true;
    } else {
      if (check) {
        parentSource.numOfCheckedChilds++;
        if (parentSource.numOfCheckedChilds === parentSource.numOfChilds) {
          parentSource.checked = true;
          parentSource.indeterminate = false;
        } else {
          parentSource.indeterminate = true;
        }
      } else {
        previousStatus && parentSource.numOfCheckedChilds--;
        previousStatus = getPreviousStatus(parentSource);
        if (
          parentSource.numOfCheckedChilds === 0 &&
          every(item => !item.source.indeterminate, parent.data.childNodes)
        ) {
          parentSource.checked = false;
          parentSource.indeterminate = false;
        } else {
          parentSource.indeterminate = true;
        }
      }
    }

    if (!isNil(parent.parent)) {
      updateParentNode(parent.parent, parentSource.checked, previousStatus, parentSource.indeterminate);
    }
  }
}

/**
 * Update interactive tree
 * @param {Array} node
 * @param {Object} parent
 * @param {Object} tree
 */
export function updateInteractiveTree(node: any, parent: any, tree: any) {
  let singleTree = buildInteractiveTree(node);
  singleTree.interacted = true;
  while (parent !== null) {
    singleTree = buildInteractiveTree(parent.data, singleTree);
    parent = parent.parent;
  }

  recursiveUpdateInteractiveTree(tree, singleTree);
}

/**
 * Build interactive tree
 * @param  {Object} parent
 * @param  {Object} child
 * @return {Object}
 */
function buildInteractiveTree(parent: any, child: any) {
  const newItem = pick(
    ['id', 'name', 'checked', 'interacted', 'indeterminate', 'numOfCheckedChilds', 'uncheckedSSO'],
    parent.source
  );
  newItem.childNodes = child ? [child] : [];

  return newItem;
}

/**
 * Recursive update interactive tree
 * @param  {Array} caches
 * @param  {Object} treeItem
 */
function recursiveUpdateInteractiveTree(caches: Array<any>, treeItem: any) {
  const cacheItem = find(item => item.id === treeItem.id, caches);
  if (cacheItem) {
    cacheItem.checked = treeItem.checked;
    cacheItem.interacted = cacheItem.interacted || treeItem.interacted;
    cacheItem.indeterminate = treeItem.indeterminate;
    cacheItem.numOfCheckedChilds = treeItem.numOfCheckedChilds;

    if (isEmpty(cacheItem.childNodes)) {
      cacheItem.childNodes = treeItem.childNodes;
    } else if (isEmpty(treeItem.childNodes)) {
      cacheItem.childNodes = [];
    } else {
      recursiveUpdateInteractiveTree(cacheItem.childNodes, treeItem.childNodes[0]);
    }
  } else {
    caches.push(treeItem);
  }
}



// WEBPACK FOOTER //
// ./ui/share/utils/Izenda.LazyLoadingUtils.js